import React, { useContext, useEffect } from "react";
import { BrowserRouter as Router, Route, Routes, Navigate } from "react-router-dom";
import { ThemeProvider, CssBaseline } from "@mui/material";
import darkTheme from "./style/theme";

import { OrganisationProvider } from "./contexts/OrganisationContext";
import { AuthContext, AuthProvider } from "./contexts/AuthContext";
import { PermissionsProvider } from "./contexts/PermissionsContext";
import { SceneProvider } from "./contexts/SceneContext";
import { RendererProvider } from "./contexts/RendererContex";
import { SessionProvider } from "./contexts/SessionContext";

import Dashboard from "./pages/dashboard/Dashboard";
import Login from "./pages/login/login";
import ProtectedRoute from "./pages/login/ProtectedRoute";
import ShortUrlBridge from "./pages/ShortUrlBridge";
import UserManagement from "./pages/dashboard/UserManagement";
import GalleryManagement from "./pages/dashboard/GalleryManagement";
import GalleryDetails from "./pages/dashboard/GalleryDetails";
import SessionManagement from "./pages/dashboard/SessionManagement";
import SessionDetails from "./pages/dashboard/SessionDetails";
import SceneControls from "./pages/dashboard/SceneControls";
import RendererManagement from "./pages/dashboard/RendererManagement";
import Organization from "./pages/dashboard/Organization.component";
import RolesManagement from "./pages/dashboard/RoleManagement";
import SceneManagement from "./pages/dashboard/SceneManagement";
import ResetPassword from "./pages/dashboard/ResetPassword";
import MyApps from "./pages/dashboard/MyApps";
import SceneAssetsPage from "./pages/scene/SceneAssetsPage";
import SceneDetailsPage from "./pages/scene/sceneDetails.page";
import CaptionDetailPage from "./pages/scene/CaptionDetailPage";
import { AlertProvider } from "./contexts/AlertContext";
import { ConfirmProvider } from "./components/common/ConfirmDialog";
// import { initializeAWSServices } from "./config/aws-config";
import { ActivityProvider } from './contexts/ActivityTrackingContext';
import NavigationTracker from "./utils/navigation-tracker";
import AssetMonitorComponent from "./components/common/AssetTracker";
import ActivityLogs from "./pages/dashboard/ActivityLogs";
import AdminPortal from './components/AdminPortal/AdminPortal';
import AdminCompetitonPage from "./components/AdminPortal/AdminPortalCompetitonPage";
import AdminSeasonsPage from "./components/AdminPortal/AdminSeasonPage";
import AdminFixturesPage from "./components/AdminPortal/AdminFixturesPage";
import AdminFixtureTeamPage from "./components/AdminPortal/AdminFixtureTeamPage";
import AdminPortalCompetitonPage from "./components/AdminPortal/AdminPortalCompetitonPage";

function App() {

  if (process.env.REACT_APP_ENV === 'PROD') {
    console.log = () => { };
    console.warn = () => { };
    console.error = () => { };
    console.info = () => { };
    console.debug = () => { };
  }
  // useEffect(() => {
  //   initializeAWSServices();
  // }, []);


  function DefaultRedirect() {
    const { user } = useContext(AuthContext);
    if (user === null) return (<Navigate to="/login" replace />);
    return user ? <Navigate to="/dashboard/renderer-management" /> : <Navigate to="/login" replace />;
  }

  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />
      <AuthProvider>
      <PermissionsProvider>
      <ActivityProvider>
        <AssetMonitorComponent />
        <OrganisationProvider>
          <SceneProvider>
            <RendererProvider>
              <AlertProvider>
                <ConfirmProvider>
                <SessionProvider>
                  <Router>
                    <NavigationTracker />
                    <Routes>
                      <Route path="/" element={<DefaultRedirect />} />

                      {/* Public Routes */}
                      <Route path="/login" element={<Login />} />
                      <Route path="/short-url-bridge" element={<ShortUrlBridge />} />
                      {/* Protected Routes */}
                      <Route element={<ProtectedRoute />}>
                        <Route path="/dashboard" element={<Dashboard />}>
                          <Route path="scene-controls" element={<SceneControls />} />
                          <Route path="scene-controls/:rendererId" element={<SceneControls />} />
                          <Route path="user-management" element={<UserManagement />} />
                          <Route path="my-apps" element={<MyApps />} />
                          <Route path="renderer-management" element={<RendererManagement />} />
                          <Route path="organisation" element={<Organization />} />
                          <Route path="roles" element={<RolesManagement />} />
                          <Route path="scene-management" element={<SceneManagement />} />
                          <Route path="scene-management/:orgId/:sceneId/edit" element={<SceneDetailsPage />} />
                          <Route path="scene-management/:orgId/:sceneId/assets/*" element={<SceneAssetsPage />} />
                          <Route path="scene-management/:orgId/:sceneId/captions/:captionId" element={<CaptionDetailPage />} />
                          <Route path="scene-management/:orgId/:sceneId/captions/:captionId/assets/*" element={<CaptionDetailPage />} />
                          <Route path="scene-management/:orgId/:sceneId/captions/:captionId/subaction/:subActionFile" element={<CaptionDetailPage />} />
                          <Route path="scene-management/:orgId/:sceneId/captions/:captionId/subaction/:subActionFile/assets/*" element={<CaptionDetailPage />} />
                          <Route path="gallery-management" element={<GalleryManagement />} />
                          <Route path="galleries/:id" element={<GalleryDetails />} />
                          <Route path="session-management" element={<SessionManagement />} />
                          <Route path="session-management/:id" element={<SessionDetails />} />
                          <Route path="reset-password" element={<ResetPassword />} />
                          <Route path="activity-logs" element={<ActivityLogs />} />
                          {/* OPUS-1045: Settings was only an app editor — that moved into
                              My Apps, so /settings now redirects there (keeps old links alive). */}
                          <Route path="settings" element={<Navigate to="/dashboard/my-apps" replace />} />
                          <Route path="admin-portal" element={<AdminPortal />}>
                            {/* Competitions */}
                            <Route
                              path="competitions"
                              element={<AdminPortalCompetitonPage />}
                            />

                            {/* Seasons */}
                            <Route
                              path="competitions/:competitionId/seasons"
                              element={<AdminSeasonsPage />}
                            />

                            {/* Fixtures */}
                            <Route
                              path="competitions/:competitionId/seasons/:seasonId/fixtures"
                              element={<AdminFixturesPage />}
                            />

                            {/* Fixture Team Setup */}
                            <Route
                              path="competitions/:competitionId/seasons/:seasonId/fixtures/:fixtureId/teams"
                              element={<AdminFixtureTeamPage />}
                            />
                          </Route>
                        </Route>
                        <Route path="reset-password" element={<ResetPassword />} />
                      </Route>
                    </Routes>
                  </Router>
                </SessionProvider>
                </ConfirmProvider>
              </AlertProvider>
            </RendererProvider>
          </SceneProvider>
        </OrganisationProvider>
      </ActivityProvider>
      </PermissionsProvider>
    </AuthProvider>
    </ThemeProvider>
  );
}

export default App;